/**
 * `<SchemaForm>` — phase 27 Slice B JSON-Schema-aware form renderer.
 *
 * Walks a `FormTreeNode` (produced by the backend compiler at
 * `apps/dashboard/src/schema-form/compiler.ts`) and renders a typed
 * form of per-field inputs (a `<div>` tree, not a `<form>` element:
 * the Dispatch button runs the check and calls `onSubmit`). Replaces
 * the phase 11 JSON textarea factor + the hand-maintained
 * `EXAMPLE_INPUTS` map.
 *
 * Locked sub-decisions (phase 27 Slice B):
 *
 *   - **Hand-rolled, no library** (B1). Layer's input schemas are
 *     small enough that a vanilla React renderer fits inside one
 *     phase commit.
 *   - **Required fields**: red asterisk after the label.
 *   - **PII fields**: subtle `[PII]` tag next to the label (sourced
 *     from the action's `pii_fields` registry entry — first time the
 *     dashboard renders piiFields semantically).
 *   - **Smart defaults**: each leaf's initial value comes from
 *     `generateSmartDefault(node, fieldName)`.
 *   - **Client-side validation**: required-populated + pattern-match +
 *     min/max length + enum-bounds + integer-range, through
 *     `@layer/dashboard/schema-form/validate` (the chokepoint's dialect;
 *     it walks nested objects and array items too, and a nested leaf's
 *     error shows under its root field with the item path). The text input
 *     carries NO native `pattern` or `maxLength` (the integer input keeps
 *     `min` / `max`, which no dialect splits on): the browser reads those
 *     two in another dialect — `pattern` under the `v` flag, which could
 *     not compile 170 of the registry's 592 patterns on 2026-10-29, so it
 *     ignored them; `maxLength` in UTF-16 code units, an emoji counting
 *     twice — and with no `<form>` element and no `:invalid` styling the
 *     `pattern` attribute never acted, while the native `maxLength` only
 *     capped typing: past a bound the form now answers on Dispatch
 *     (schema-form-native-attrs, 2026-10-30), in the server's own words
 *     since field-errors (2026-11-01): "Use at most N characters", and a
 *     format by what to type rather than its pattern. Hint-only — server
 *     stays authoritative.
 *   - **"Edit as JSON" toggle** (B6): below the rendered form, a
 *     button swaps the form for a JSON textarea bound to the same
 *     state. Per-card + per-session — no persistence.
 *
 * The component is intentionally local-state-only: it owns the form
 * value internally + emits a fresh value on submit. No automated
 * tests at phase 27 (frontend test infra is OUT OF SCOPE per the
 * brief); manual smoke verification per the runbook covers the 5
 * representative actions.
 */

import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type ReactElement,
} from "react";
import type {
  FormArrayNode,
  FormBooleanNode,
  FormEnumNode,
  FormIntegerNode,
  FormObjectNode,
  FormOneOfNode,
  FormTextNode,
  FormTreeNode,
} from "@layer/dashboard/schema-form/compiler";
import { generateSmartDefault } from "@layer/dashboard/schema-form/defaults";
import {
  errorForField,
  validateAgainstTree,
  withoutFieldErrors,
} from "@layer/dashboard/schema-form/validate";
import { api } from "../lib/api";
import { useLookupRefresh } from "../lib/lookup-refresh";
import { useCachedResource } from "../lib/resource-cache";
import {
  isoToZonedDatetimeInput,
  zonedDatetimeInputToIso,
} from "../lib/studio-tz";

/** Root form tree — Layer's input schemas are either `object` or `oneOf`. */
type RootTree = FormObjectNode | FormOneOfNode;

// ---------------------------------------------------------------------------
// Public component shape.
// ---------------------------------------------------------------------------

export interface SchemaFormProps {
  readonly tree: RootTree;
  /**
   * PII field names from the action's `pii_fields` registry entry.
   * Used to render the `[PII]` tag next to the matching labels.
   */
  readonly piiFields: ReadonlyArray<string>;
  /**
   * Submit handler — called with the validated form value. The parent
   * card uses this to invoke `useDispatch(...).run(value)`.
   */
  readonly onSubmit: (value: Record<string, unknown>) => void;
  /**
   * Whether the dispatch button is disabled (e.g. loading state from
   * the parent's `useDispatch`).
   */
  readonly busy: boolean;
  /**
   * Optional confirmation prompt — when set, clicking Submit shows a
   * modal that requires the user to type the literal `confirmKey`
   * string before the actual submit fires. Used by `delete_member`
   * (sub-decision B7).
   */
  readonly confirmDispatch?: {
    readonly prompt: string;
    readonly confirmKey: string;
  };
}

export function SchemaForm({
  tree,
  piiFields,
  onSubmit,
  busy,
  confirmDispatch,
}: SchemaFormProps): ReactElement {
  const piiSet = useMemo(() => new Set(piiFields), [piiFields]);
  // For oneOf roots, track which variant is active; the active variant's
  // object node is what the renderer + validator walk.
  const [selectedVariant, setSelectedVariant] = useState<number>(0);
  const activeObject: FormObjectNode = useMemo(() => {
    if (tree.kind === "object") return tree;
    const v = tree.variants[selectedVariant] ?? tree.variants[0];
    return (
      v?.node ?? {
        kind: "object",
        required: [],
        properties: {},
      }
    );
  }, [tree, selectedVariant]);
  const initial = useMemo(
    () =>
      generateSmartDefault(activeObject, "") as Record<string, unknown>,
    [activeObject],
  );
  const [value, setValue] = useState<Record<string, unknown>>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [jsonMode, setJsonMode] = useState<boolean>(false);
  const [jsonText, setJsonText] = useState<string>(() =>
    JSON.stringify(initial, null, 2),
  );
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<boolean>(false);
  const [confirmInput, setConfirmInput] = useState<string>("");

  // When the active variant changes, reset value to its defaults so
  // stale fields from the prior branch don't leak into the dispatch.
  useEffect(() => {
    setValue(initial);
    setErrors({});
  }, [initial]);

  // Keep JSON textarea in sync when the form value changes via the
  // typed path (so toggling to JSON shows the current state).
  useEffect(() => {
    setJsonText(JSON.stringify(value, null, 2));
  }, [value]);

  const setField = (name: string, v: unknown): void => {
    setValue((prev) => {
      if (v === undefined || v === "") {
        // Empty-string on an optional field → omit the key so
        // `additionalProperties: false` schemas with optional fields
        // don't get spurious empty values.
        const isRequired = activeObject.required.includes(name);
        if (!isRequired && (v === undefined || v === "")) {
          const out = { ...prev };
          delete out[name];
          return out;
        }
      }
      return { ...prev, [name]: v };
    });
    if (errorForField(errors, name) !== undefined) {
      // the field's own error and any below it (an array item, a nested field)
      setErrors((prev) => withoutFieldErrors(prev, name));
    }
  };

  const handleSubmit = (): void => {
    const submitValue = jsonMode ? safeParseJson(jsonText) : value;
    if (jsonMode && submitValue === undefined) return;
    const validationErrors = jsonMode
      ? {}
      : validateAgainstTree(activeObject, value);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    if (confirmDispatch !== undefined) {
      setPendingConfirm(true);
      return;
    }
    onSubmit(submitValue as Record<string, unknown>);
  };

  const confirmAndSubmit = (): void => {
    if (
      confirmDispatch !== undefined &&
      confirmInput !== confirmDispatch.confirmKey
    )
      return;
    setPendingConfirm(false);
    setConfirmInput("");
    const submitValue = jsonMode ? safeParseJson(jsonText) : value;
    onSubmit(submitValue as Record<string, unknown>);
  };

  const safeParseJson = (text: string): unknown => {
    try {
      const parsed = JSON.parse(text) as unknown;
      if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        setJsonError("Input must be a JSON object");
        return undefined;
      }
      setJsonError(null);
      return parsed;
    } catch (e) {
      setJsonError(`Invalid JSON — ${(e as Error).message}`);
      return undefined;
    }
  };

  return (
    <div className="schema-form">
      {jsonMode ? (
        <div className="schema-form-json-pane">
          <label className="schema-form-label">JSON input:</label>
          <textarea
            className="schema-form-json-textarea"
            rows={10}
            value={jsonText}
            onChange={(e) => {
              setJsonText(e.target.value);
              const parsed = safeParseJson(e.target.value);
              if (parsed !== undefined) {
                setValue(parsed as Record<string, unknown>);
              }
            }}
            spellCheck={false}
          />
          {jsonError !== null ? (
            <p className="schema-form-error">{jsonError}</p>
          ) : null}
        </div>
      ) : (
        <>
          {tree.kind === "oneOf" ? (
            <div className="schema-form-root-oneof-tabs">
              {tree.variants.map((v, idx) => (
                <button
                  type="button"
                  key={v.discriminator}
                  className={
                    idx === selectedVariant
                      ? "schema-form-oneof-tab schema-form-oneof-tab-active"
                      : "schema-form-oneof-tab"
                  }
                  onClick={() => setSelectedVariant(idx)}
                >
                  {v.discriminator}
                </button>
              ))}
            </div>
          ) : null}
          <ObjectFields
            tree={activeObject}
            value={value}
            setField={setField}
            errors={errors}
            piiSet={piiSet}
          />
        </>
      )}
      <div className="schema-form-buttons">
        <button
          type="button"
          className="action-dispatch-btn"
          onClick={handleSubmit}
          disabled={busy}
        >
          {busy ? "Dispatching…" : "Dispatch"}
        </button>
        <button
          type="button"
          className="action-dispatch-btn schema-form-toggle-btn"
          onClick={() => setJsonMode((m) => !m)}
        >
          {jsonMode ? "Edit as form" : "Edit as JSON"}
        </button>
      </div>
      {pendingConfirm && confirmDispatch !== undefined ? (
        <ConfirmModal
          prompt={confirmDispatch.prompt}
          confirmKey={confirmDispatch.confirmKey}
          input={confirmInput}
          onChangeInput={setConfirmInput}
          onCancel={() => {
            setPendingConfirm(false);
            setConfirmInput("");
          }}
          onConfirm={confirmAndSubmit}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Object-field renderer (shared by root + nested objects).
// ---------------------------------------------------------------------------

interface ObjectFieldsProps {
  readonly tree: FormObjectNode;
  readonly value: Record<string, unknown>;
  readonly setField: (name: string, v: unknown) => void;
  readonly errors: Record<string, string>;
  readonly piiSet: Set<string>;
}

function ObjectFields({
  tree,
  value,
  setField,
  errors,
  piiSet,
}: ObjectFieldsProps): ReactElement {
  const entries = Object.entries(tree.properties);
  return (
    <div className="schema-form-fields">
      {entries.map(([fieldName, node]) => (
        <FieldRow
          key={fieldName}
          fieldName={fieldName}
          node={node}
          value={value[fieldName]}
          onChange={(v) => setField(fieldName, v)}
          error={errorForField(errors, fieldName)}
          piiSet={piiSet}
        />
      ))}
    </div>
  );
}

interface FieldRowProps {
  readonly fieldName: string;
  readonly node: FormTreeNode;
  readonly value: unknown;
  readonly onChange: (v: unknown) => void;
  readonly error: string | undefined;
  readonly piiSet: Set<string>;
}

function FieldRow({
  fieldName,
  node,
  value,
  onChange,
  error,
  piiSet,
}: FieldRowProps): ReactElement {
  const isPii = piiSet.has(fieldName);
  const isRequired = (node as { required?: boolean }).required === true;
  const description = (node as { description?: string }).description;
  const label = (
    <label className="schema-form-label" htmlFor={`field-${fieldName}`}>
      <span className="schema-form-label-name">{fieldName}</span>
      {isRequired ? (
        <span className="schema-form-required" aria-hidden="true">
          *
        </span>
      ) : null}
      {isPii ? (
        <span className="schema-form-pii-tag" title="Personally Identifiable Information">
          [PII]
        </span>
      ) : null}
    </label>
  );
  return (
    <div className="schema-form-row">
      {label}
      <FieldInput
        fieldName={fieldName}
        node={node}
        value={value}
        onChange={onChange}
        piiSet={piiSet}
      />
      {description !== undefined ? (
        <p className="schema-form-hint">{description}</p>
      ) : null}
      {error !== undefined ? (
        <p className="schema-form-error">{error}</p>
      ) : null}
    </div>
  );
}

interface FieldInputProps {
  readonly fieldName: string;
  readonly node: FormTreeNode;
  readonly value: unknown;
  readonly onChange: (v: unknown) => void;
  readonly piiSet: Set<string>;
}

function FieldInput({
  fieldName,
  node,
  value,
  onChange,
  piiSet,
}: FieldInputProps): ReactElement {
  if (node.kind === "text") {
    return <TextField fieldName={fieldName} node={node} value={value} onChange={onChange} />;
  }
  if (node.kind === "integer") {
    return <IntegerField fieldName={fieldName} node={node} value={value} onChange={onChange} />;
  }
  if (node.kind === "boolean") {
    return <BooleanField fieldName={fieldName} node={node} value={value} onChange={onChange} />;
  }
  if (node.kind === "enum") {
    return <EnumField fieldName={fieldName} node={node} value={value} onChange={onChange} />;
  }
  if (node.kind === "array") {
    return (
      <ArrayField
        fieldName={fieldName}
        node={node}
        value={value}
        onChange={onChange}
        piiSet={piiSet}
      />
    );
  }
  if (node.kind === "object") {
    return (
      <NestedObjectField
        fieldName={fieldName}
        node={node}
        value={value}
        onChange={onChange}
        piiSet={piiSet}
      />
    );
  }
  if (node.kind === "oneOf") {
    return (
      <OneOfField
        fieldName={fieldName}
        node={node}
        value={value}
        onChange={onChange}
        piiSet={piiSet}
      />
    );
  }
  return <span className="schema-form-unsupported">unsupported</span>;
}

/**
 * Lookup entities whose `*_id` field renders a debounced type-ahead
 * search instead of a 50-row dropdown. Both back onto the same
 * `GET /api/lookups/<entity>?q=` server filter (display_name OR email OR
 * phone, ILIKE, across ALL rows) the Calendar's member picker uses.
 */
const SEARCHABLE_LOOKUP_ENTITIES: ReadonlySet<string> = new Set([
  "members",
  "trainers",
]);

function TextField({
  fieldName,
  node,
  value,
  onChange,
}: {
  fieldName: string;
  node: FormTextNode;
  value: unknown;
  onChange: (v: unknown) => void;
}): ReactElement {
  const stringValue = typeof value === "string" ? value : "";
  // Lookup-backed field. The widget depends on the entity:
  //   - `members` / `trainers` → a debounced type-ahead search (name /
  //     email / phone), mirroring the Calendar's "book someone in" picker
  //     — far better than a 50-row dropdown for thousands of members.
  //   - `slots` → a compact Today / Tomorrow slot picker ("little calendar
  //     widget" of clickable time buttons).
  //   - everything else → the legacy dropdown-of-50 + "Paste raw ID" toggle.
  if (node.lookupEntity !== undefined) {
    if (SEARCHABLE_LOOKUP_ENTITIES.has(node.lookupEntity)) {
      return (
        <SearchableLookupField
          fieldName={fieldName}
          entity={node.lookupEntity}
          value={stringValue}
          onChange={(v) => onChange(v)}
        />
      );
    }
    if (node.lookupEntity === "slots") {
      return (
        <SlotPickerField
          fieldName={fieldName}
          value={stringValue}
          onChange={(v) => onChange(v)}
        />
      );
    }
    return (
      <LookupSelect
        fieldName={fieldName}
        entity={node.lookupEntity}
        value={stringValue}
        onChange={(v) => onChange(v)}
      />
    );
  }
  if (node.format === "datetime") {
    return (
      <input
        id={`field-${fieldName}`}
        type="datetime-local"
        className="schema-form-input"
        step={60}
        value={isoToLocalDatetimeInput(stringValue)}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChange(localDatetimeInputToIso(e.target.value))
        }
      />
    );
  }
  const inputType = node.format === "email" ? "email" : "text";
  // No native `pattern` / `maxLength`: the browser reads them in another dialect —
  // `pattern` under the `v` flag, `maxLength` in UTF-16 code units — and the form's own
  // check answers on Dispatch instead (schema-form-native-attrs, 2026-10-30).
  return (
    <input
      id={`field-${fieldName}`}
      type={inputType}
      className="schema-form-input"
      value={stringValue}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      spellCheck={false}
    />
  );
}

/**
 * Dropdown of `{value, label}` options loaded from
 * `GET /api/lookups/<entity>` (newest 50 rows in current studio). Has
 * a "Paste raw ID" toggle that flips to a text input for IDs not in
 * the loaded page (e.g. one copied from `/data/audit_log`).
 *
 * Locked sub-decisions (Mario 2026-05-23):
 *   - Display field only (no UUID/slug shown in the option label).
 *   - Native `<select>` — browser's built-in keyboard search is the
 *     type-to-filter at MVP.
 *   - Dropdown + paste-raw-ID toggle — best of both.
 */
function LookupSelect({
  fieldName,
  entity,
  value,
  onChange,
}: {
  fieldName: string;
  entity: string;
  value: string;
  onChange: (v: string) => void;
}): ReactElement {
  const [pasteMode, setPasteMode] = useState<boolean>(false);
  // Phase 67 — options are cache-backed, keyed `lookup:<entity>`. A
  // mutation that calls `useLookupRefresh().bump()` invalidates the
  // `lookup:` prefix, so every mounted select revalidates in place
  // through the shared cache (still silent — a cached value renders
  // while the background refetch runs). `useLookupRefresh` is still
  // mounted so the provider's bump fires.
  useLookupRefresh();
  const {
    data: lookupData,
    loading: lookupLoading,
    error: lookupError,
  } = useCachedResource(`lookup:${entity}`, () => api.lookupOptions(entity));
  const options: ReadonlyArray<{ value: string; label: string }> =
    lookupData?.options ?? [];
  // First load (cold cache) shows "Loading…"; a cached value renders
  // immediately on entity switch / re-mount.
  const loading = lookupLoading;
  const error = lookupError?.message ?? null;

  // If the current value isn't in the loaded options, flip into paste
  // mode automatically (e.g. on form re-mount with a prefilled value).
  const valueInOptions = options.some((o) => o.value === value);
  const effectivePaste = pasteMode || (value !== "" && !valueInOptions);

  return (
    <div className="schema-form-lookup">
      {effectivePaste ? (
        <input
          id={`field-${fieldName}`}
          type="text"
          className="schema-form-input"
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          placeholder={`Paste ${entity} ID/slug`}
          spellCheck={false}
        />
      ) : (
        <select
          id={`field-${fieldName}`}
          className="schema-form-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={loading || error !== null}
        >
          <option value="">
            {loading
              ? `Loading ${entity}…`
              : error !== null
              ? `Error: ${error}`
              : options.length === 0
              ? `No ${entity} in this studio`
              : `Choose ${entity.replace(/_/g, " ")}…`}
          </option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
      <button
        type="button"
        className="schema-form-lookup-toggle"
        onClick={() => setPasteMode((m) => !m)}
        title={effectivePaste ? "Switch to dropdown" : "Paste a raw ID"}
      >
        {effectivePaste ? "List" : "Paste"}
      </button>
    </div>
  );
}

/**
 * Debounced type-ahead picker for `members` / `trainers` (Mario
 * 2026-06-24). Identical UX to the Calendar's "book someone in" search:
 * type a name / email / phone, matches appear in a listbox, click one to
 * commit. The chosen row shows as a chip with a "Change" affordance.
 * A "Paste" toggle keeps raw-ID entry available for IDs that aren't in
 * the result set.
 */
function SearchableLookupField({
  fieldName,
  entity,
  value,
  onChange,
}: {
  fieldName: string;
  entity: string;
  value: string;
  onChange: (v: string) => void;
}): ReactElement {
  const [query, setQuery] = useState<string>("");
  const [options, setOptions] = useState<
    ReadonlyArray<{ value: string; label: string; sublabel?: string }> | null
  >(null);
  const [searching, setSearching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [chosenLabel, setChosenLabel] = useState<string>("");
  const [pasteMode, setPasteMode] = useState<boolean>(false);

  // Debounced server-side search. Results stay hidden until something is
  // typed; an empty query clears the list (mirrors the Calendar picker).
  useEffect(() => {
    const q = query.trim();
    if (q === "") {
      setOptions(null);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const handle = setTimeout(() => {
      void (async () => {
        try {
          const res = await api.lookupOptions(entity, q);
          if (!cancelled) {
            setOptions(res.options);
            setError(null);
          }
        } catch (e) {
          if (!cancelled) {
            setOptions([]);
            setError((e as Error).message);
          }
        } finally {
          if (!cancelled) setSearching(false);
        }
      })();
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, entity]);

  const commit = (v: string): void => {
    if (v === "") return;
    const opt = options?.find((o) => o.value === v);
    onChange(v);
    setChosenLabel(
      opt
        ? `${opt.label}${opt.sublabel !== undefined ? ` · ${opt.sublabel}` : ""}`
        : v,
    );
    setQuery("");
    setOptions(null);
  };

  const noun = entity === "trainers" ? "trainers" : "members";

  if (pasteMode) {
    return (
      <div className="schema-form-lookup">
        <input
          id={`field-${fieldName}`}
          type="text"
          className="schema-form-input"
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          placeholder={`Paste ${entity} ID`}
          spellCheck={false}
        />
        <button
          type="button"
          className="schema-form-lookup-toggle"
          onClick={() => setPasteMode(false)}
          title="Switch to search"
        >
          Search
        </button>
      </div>
    );
  }

  // A value is committed → show the chosen chip with a Change button.
  if (value !== "" && chosenLabel !== "") {
    return (
      <div className="schema-form-search">
        <div className="schema-form-search-chosen">
          <span className="schema-form-search-chosen-label">{chosenLabel}</span>
          <button
            type="button"
            className="schema-form-search-change"
            onClick={() => {
              onChange("");
              setChosenLabel("");
            }}
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="schema-form-search">
      <div className="schema-form-lookup">
        <input
          id={`field-${fieldName}`}
          type="text"
          className="schema-form-input"
          placeholder={`Search ${noun} by name, email, or phone…`}
          value={query}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          spellCheck={false}
          autoComplete="off"
        />
        <button
          type="button"
          className="schema-form-lookup-toggle"
          onClick={() => setPasteMode(true)}
          title="Paste a raw ID"
        >
          Paste
        </button>
      </div>
      {query.trim().length > 0 ? (
        <select
          className="schema-form-search-list"
          value={value}
          size={6}
          onChange={(e) => commit(e.target.value)}
          onClick={(e) => {
            // Native listbox: a click targets the <option>; commit directly
            // so the auto-highlighted first row (which fires no change event)
            // still selects on the first click.
            const t = e.target;
            if (t instanceof HTMLOptionElement) commit(t.value);
          }}
        >
          {options === null || searching ? (
            <option value="">Searching…</option>
          ) : error !== null ? (
            <option value="">Error: {error}</option>
          ) : options.length === 0 ? (
            <option value="">No matches</option>
          ) : (
            options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
                {o.sublabel !== undefined ? ` · ${o.sublabel}` : ""}
              </option>
            ))
          )}
        </select>
      ) : null}
    </div>
  );
}

/**
 * Slot picker (Mario 2026-06-24): a `slot_id` field renders the active
 * slots for TODAY and TOMORROW (studio-local; scoped server-side) as
 * clickable time buttons grouped under a day heading — the "little
 * calendar widget" instead of a dropdown. A "Paste ID" toggle keeps a
 * raw slot id reachable for a slot outside the two-day window.
 */
function SlotPickerField({
  fieldName,
  value,
  onChange,
}: {
  fieldName: string;
  value: string;
  onChange: (v: string) => void;
}): ReactElement {
  useLookupRefresh();
  const {
    data: lookupData,
    loading,
    error,
  } = useCachedResource("lookup:slots", () => api.lookupOptions("slots"));
  const [pasteMode, setPasteMode] = useState<boolean>(false);
  const options = lookupData?.options ?? [];

  const valueInOptions = options.some((o) => o.value === value);
  const effectivePaste = pasteMode || (value !== "" && !valueInOptions);

  // Preserve catalog order (slots arrive sorted by start time) while
  // bucketing into day groups for the section headings.
  const groups = useMemo(() => {
    const order: string[] = [];
    const byGroup = new Map<
      string,
      Array<{ value: string; label: string; sublabel?: string }>
    >();
    for (const o of options) {
      const g = o.group ?? "Slots";
      let bucket = byGroup.get(g);
      if (bucket === undefined) {
        bucket = [];
        byGroup.set(g, bucket);
        order.push(g);
      }
      bucket.push(o);
    }
    return order.map((g) => ({ group: g, items: byGroup.get(g) ?? [] }));
  }, [options]);

  if (effectivePaste) {
    return (
      <div className="schema-form-lookup">
        <input
          id={`field-${fieldName}`}
          type="text"
          className="schema-form-input"
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          placeholder="Paste slot ID"
          spellCheck={false}
        />
        <button
          type="button"
          className="schema-form-lookup-toggle"
          onClick={() => setPasteMode(false)}
          title="Switch to slot picker"
        >
          Pick
        </button>
      </div>
    );
  }

  return (
    <div className="schema-form-slotpick">
      {loading ? (
        <p className="schema-form-slotpick-empty">Loading slots…</p>
      ) : error !== null ? (
        <p className="schema-form-slotpick-empty">Error: {error.message}</p>
      ) : options.length === 0 ? (
        <p className="schema-form-slotpick-empty">
          No active slots today or tomorrow.
        </p>
      ) : (
        groups.map((g) => (
          <div key={g.group} className="schema-form-slotpick-day">
            <div className="schema-form-slotpick-day-head">{g.group}</div>
            <div className="schema-form-slotpick-times">
              {g.items.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={
                    o.value === value
                      ? "schema-form-slotpick-time schema-form-slotpick-time-on"
                      : "schema-form-slotpick-time"
                  }
                  onClick={() => onChange(o.value)}
                  title={o.sublabel}
                >
                  <span className="schema-form-slotpick-hm">{o.label}</span>
                  {o.sublabel !== undefined ? (
                    <span className="schema-form-slotpick-sub">{o.sublabel}</span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>
        ))
      )}
      <button
        type="button"
        className="schema-form-lookup-toggle schema-form-slotpick-paste"
        onClick={() => setPasteMode(true)}
        title="Paste a raw slot ID"
      >
        Paste ID
      </button>
    </div>
  );
}

/**
 * Convert a stored ISO-8601 string (`YYYY-MM-DDTHH:mm:ss.sssZ` or with
 * offset) into the `<input type="datetime-local">` value shape
 * (`YYYY-MM-DDTHH:mm`, in local time). Empty / malformed input → empty
 * string so the picker shows its placeholder.
 */
function isoToLocalDatetimeInput(iso: string): string {
  // Render the wall-clock in the STUDIO timezone, NOT the
  // browser's — so an operator on any machine sees/enters studio-local time.
  return isoToZonedDatetimeInput(iso);
}

/**
 * Convert a `<input type="datetime-local">` value (`YYYY-MM-DDTHH:mm`,
 * interpreted as STUDIO-local time) into the ISO-8601 UTC shape the canonical
 * actions expect (`YYYY-MM-DDTHH:mm:ss.sssZ`). Pinned to the studio timezone
 * (not the browser's) so e.g. 7pm always stores as 18:00Z in BST, never 19:00Z.
 */
function localDatetimeInputToIso(local: string): string {
  return zonedDatetimeInputToIso(local);
}

function IntegerField({
  fieldName,
  node,
  value,
  onChange,
}: {
  fieldName: string;
  node: FormIntegerNode;
  value: unknown;
  onChange: (v: unknown) => void;
}): ReactElement {
  const numValue =
    typeof value === "number"
      ? String(value)
      : typeof value === "string"
      ? value
      : "";
  return (
    <input
      id={`field-${fieldName}`}
      type="number"
      className="schema-form-input"
      value={numValue}
      onChange={(e: ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        if (raw === "") {
          onChange(undefined);
          return;
        }
        const parsed = Number.parseInt(raw, 10);
        if (Number.isFinite(parsed)) onChange(parsed);
      }}
      {...(node.minimum !== undefined ? { min: node.minimum } : {})}
      {...(node.maximum !== undefined ? { max: node.maximum } : {})}
    />
  );
}

function BooleanField({
  fieldName,
  value,
  onChange,
}: {
  fieldName: string;
  node: FormBooleanNode;
  value: unknown;
  onChange: (v: unknown) => void;
}): ReactElement {
  return (
    <input
      id={`field-${fieldName}`}
      type="checkbox"
      className="schema-form-checkbox"
      checked={value === true}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
    />
  );
}

function EnumField({
  fieldName,
  node,
  value,
  onChange,
}: {
  fieldName: string;
  node: FormEnumNode;
  value: unknown;
  onChange: (v: unknown) => void;
}): ReactElement {
  // Optional enums must NOT pre-select the first value: the <select>
  // would DISPLAY it as chosen while the form state has no key (no
  // onChange fires when you "pick" the already-shown option), so the
  // field is silently dropped from the dispatch. Render a leading blank
  // option and default to unselected; only required enums keep a
  // first-value default (the smart-default generator seeds it into
  // state so it's a real, submitted value).
  const stringValue =
    typeof value === "string"
      ? value
      : node.required
        ? node.values[0] ?? ""
        : "";
  return (
    <select
      id={`field-${fieldName}`}
      className="schema-form-input"
      value={stringValue}
      onChange={(e) => onChange(e.target.value)}
    >
      {!node.required ? <option value="">— none —</option> : null}
      {node.values.map((v) => (
        <option key={v} value={v}>
          {v}
        </option>
      ))}
    </select>
  );
}

function ArrayField({
  fieldName,
  node,
  value,
  onChange,
  piiSet,
}: {
  fieldName: string;
  node: FormArrayNode;
  value: unknown;
  onChange: (v: unknown) => void;
  piiSet: Set<string>;
}): ReactElement {
  const items = Array.isArray(value) ? (value as unknown[]) : [];
  return (
    <div className="schema-form-array">
      {items.map((item, idx) => (
        <div key={idx} className="schema-form-array-row">
          <FieldInput
            fieldName={`${fieldName}[${idx}]`}
            node={node.itemNode}
            value={item}
            onChange={(v) => {
              const next = [...items];
              next[idx] = v;
              onChange(next);
            }}
            piiSet={piiSet}
          />
          <button
            type="button"
            className="schema-form-remove-btn"
            onClick={() => {
              const next = [...items];
              next.splice(idx, 1);
              onChange(next);
            }}
          >
            Remove
          </button>
        </div>
      ))}
      <button
        type="button"
        className="schema-form-add-btn"
        onClick={() =>
          onChange([...items, generateSmartDefault(node.itemNode, fieldName)])
        }
      >
        + Add item
      </button>
    </div>
  );
}

function NestedObjectField({
  fieldName,
  node,
  value,
  onChange,
  piiSet,
}: {
  fieldName: string;
  node: FormObjectNode;
  value: unknown;
  onChange: (v: unknown) => void;
  piiSet: Set<string>;
}): ReactElement {
  const objValue =
    typeof value === "object" && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  return (
    <fieldset className="schema-form-nested">
      <legend className="schema-form-nested-legend">{fieldName}</legend>
      <ObjectFields
        tree={node}
        value={objValue}
        setField={(name, v) => onChange({ ...objValue, [name]: v })}
        errors={{}}
        piiSet={piiSet}
      />
    </fieldset>
  );
}

function OneOfField({
  fieldName,
  node,
  value,
  onChange,
  piiSet,
}: {
  fieldName: string;
  node: FormOneOfNode;
  value: unknown;
  onChange: (v: unknown) => void;
  piiSet: Set<string>;
}): ReactElement {
  const [selected, setSelected] = useState<number>(0);
  const variant = node.variants[selected];
  const objValue =
    typeof value === "object" && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  if (variant === undefined)
    return <span className="schema-form-unsupported">no variants</span>;
  return (
    <div className="schema-form-oneof">
      <div className="schema-form-oneof-tabs">
        {node.variants.map((v, idx) => (
          <button
            type="button"
            key={v.discriminator}
            className={
              idx === selected
                ? "schema-form-oneof-tab schema-form-oneof-tab-active"
                : "schema-form-oneof-tab"
            }
            onClick={() => {
              setSelected(idx);
              // Reset value to the selected variant's defaults so
              // stale fields from the prior branch don't leak.
              onChange(generateSmartDefault(v.node, fieldName));
            }}
          >
            {v.discriminator}
          </button>
        ))}
      </div>
      <ObjectFields
        tree={variant.node}
        value={objValue}
        setField={(name, v) => onChange({ ...objValue, [name]: v })}
        errors={{}}
        piiSet={piiSet}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirm modal (sub-decision B7 — delete_member).
// ---------------------------------------------------------------------------

interface ConfirmModalProps {
  readonly prompt: string;
  readonly confirmKey: string;
  readonly input: string;
  readonly onChangeInput: (v: string) => void;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

function ConfirmModal({
  prompt,
  confirmKey,
  input,
  onChangeInput,
  onCancel,
  onConfirm,
}: ConfirmModalProps): ReactElement {
  const enabled = input === confirmKey;
  return (
    <div className="schema-form-confirm-overlay" role="dialog" aria-modal="true">
      <div className="schema-form-confirm-modal">
        <p className="schema-form-confirm-prompt">{prompt}</p>
        <input
          type="text"
          className="schema-form-input"
          value={input}
          onChange={(e) => onChangeInput(e.target.value)}
          autoFocus
          placeholder={`Type ${confirmKey} to confirm`}
          spellCheck={false}
        />
        <div className="schema-form-confirm-buttons">
          <button
            type="button"
            className="schema-form-confirm-btn"
            disabled={!enabled}
            onClick={onConfirm}
          >
            Confirm
          </button>
          <button
            type="button"
            className="schema-form-confirm-cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Client-side validation lives in `@layer/dashboard/schema-form/validate` (hint-only —
// server stays authoritative); it walks nested objects and array items too.
